/*
Author: Chathuraka Mallawa Arachchi
Date: 18/10/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for managing frm state including validations.
*/

import {
    patientQuestionnaireDetails,
    poaQuestionnaireDetails,
} from '@/components/survey/SurveyModel'
import { INode } from '@/components/survey/SurveyModel/types/nodeTypes'
import { PatientAnswer } from '@/components/survey/SurveyModel/types/patientTypes'
import { PoaAnswer } from '@/components/survey/SurveyModel/types/poaTypes'
import { Validate } from '@/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import {
    FieldState,
    FormState,
    initialState,
} from '../../components/survey/SurveyModel/types/commonTypes'

const formSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        updateField: (
            state,
            action: PayloadAction<{
                field: keyof FormState
                value: string | boolean
            }>
        ) => {
            const { field, value } = action.payload
            state[field].value = value
        },
        addNewField: (
            state,
            action: PayloadAction<{
                field: keyof FormState
                value: FieldState
            }>
        ) => {
            const { field, value } = action.payload
            state[field] = value
        },
        removeField: (
            state,
            action: PayloadAction<{
                field: keyof FormState
            }>
        ) => {
            const { field } = action.payload
            delete state[field]
        },
        applyFieldsForNewNodes: (
            // ToDO: this function is called by patient and poa.
            state,
            action: PayloadAction<{
                nodes: INode
                answer: PatientAnswer[]
            }>
        ) => {
            const { nodes, answer } = action.payload
            const form: FormState =
                patientQuestionnaireDetails.generateQuestionnaireDetailsForm(
                    answer,
                    nodes,
                    state
                )
            return form
        },
        applyFieldsForNewPoaNodes: (
            // ToDO: this function is called by patient and poa.
            state,
            action: PayloadAction<{
                nodes: INode
                answer: PoaAnswer[]
            }>
        ) => {
            const { nodes, answer } = action.payload
            const form: FormState =
                poaQuestionnaireDetails.generateQuestionnaireDetailsForm(
                    answer,
                    nodes,
                    state
                )
            return form
        },
        validateField: (
            state,
            action: PayloadAction<{
                name: string
                value: any
                dirty: boolean
            }>
        ) => {
            const { name, value, dirty } = action.payload
            const form = Validate.input(name, value, state, dirty)
            return form
        },
        updateForm: (_state, action) => {
            return action.payload // Replaces the entire form state
        },
    },
})

export const {
    updateField,
    addNewField,
    updateForm,
    applyFieldsForNewNodes,
    applyFieldsForNewPoaNodes,
    validateField,
    removeField,
} = formSlice.actions

export const useUpdateField = () => {
    const dispatch = useDispatch()

    const updateFieldValue = (
        field: keyof FormState,
        value: string | boolean
    ) => {
        dispatch(updateField({ field, value }))
    }

    return updateFieldValue
}

export const useValidate = () => {
    const dispatch = useDispatch()

    const validateInputField = (name: string, value: any, dirty: boolean) => {
        dispatch(validateField({ name, value, dirty }))
    }
    return validateInputField
}

export const useApplyFieldsForNewNodes = () => {
    const dispatch = useDispatch()

    const applyNewFieldValues = (nodes: INode, answer: PatientAnswer[]) => {
        dispatch(applyFieldsForNewNodes({ nodes, answer }))
    }

    return applyNewFieldValues
}

export const useApplyFieldsForNewPoaNodes = () => {
    const dispatch = useDispatch()

    const applyNewFieldValues = (nodes: INode, answer: PoaAnswer[]) => {
        dispatch(applyFieldsForNewPoaNodes({ nodes, answer }))
    }

    return applyNewFieldValues
}

export const useAddNewField = () => {
    const dispatch = useDispatch()

    const addNewFieldValue = (field: keyof FormState, value: FieldState) => {
        dispatch(addNewField({ field, value }))
    }

    return addNewFieldValue
}

export const useDeleteField = () => {
    const dispatch = useDispatch()

    const deleteField = (field: keyof FormState) => {
        dispatch(removeField({ field }))
    }

    return deleteField
}

export const useUpdateForm = () => {
    const dispatch = useDispatch()

    const updateFormData = (value: FieldState) => {
        dispatch(updateForm(value))
    }

    return updateFormData
}

export default formSlice.reducer
